import * as _ from 'lodash';
import * as queryString from 'query-string';
import { diagnostics, utils } from 'ui-common';
import { hashHistory } from 'react-router';

import { updateAttemptedRoute } from 'login-actions/globalActions';
import * as deploymentConfig from 'login-utils/deploymentConfig';
import * as featureFlags from './featureFlags';
import * as globalWrappers from './globalWrappers';
import * as userUtils from './userUtils';
import { LOGIN_METHODS } from './loginUtils';
import assert from './assert';
import States, { StatesRich } from 'login-constants/states';

const { cookieUtils } = utils;

export const isAtURL = (url: string) => {
  // This is mostly for test code.
  if (!url) {
    return false;
  }

  assert(_.startsWith(url, '/'), `Must provide a relative path to isAtURL for url: ${url}`);
  return _.startsWith(globalWrappers.getLocation().hash, `#${url}`);
};

export function getValidStateFromQuery(query: string) {
  const parsedQuery = queryString.parse(query);

  const resolvedState = (() => {
    if (parsedQuery.activation) {
      return States.ACTIVATION_PASSWORD_LENDER;
    } else if (parsedQuery.reset) {
      return States.CHOOSE_RESET_PASSWORD;
    } else {
      return null;
    }
  })();

  if (!resolvedState) {
    return null;
  }

  return `/${resolvedState}${query}`;
}

function isLoanOfficerPath(path: string) {
  const topLevel = _.nth(path.split('/'), 1);
  if (topLevel === 'loan-officers') {
    return (
      featureFlags.isEnabled('loanOfficerPage_09182018') &&
      _.get(deploymentConfig.get(), 'loanOfficerPages.isEnabled') &&
      (_.get(deploymentConfig.get(), 'allowSignup') ||
        _.get(deploymentConfig.get(), 'loanOfficerPages.enableApplyNowButtonRedirect'))
    );
  }
  return false;
}

/*
Goes to a state, but retains the query params of the state that it's current in.
*/
export function pushWithContext(path: string) {
  const currentLocation = hashHistory.getCurrentLocation();
  hashHistory.push({
    pathname: `/${path}`,
    search: currentLocation.search,
  });
}

type FlagEnabledFn = (nextState) => void;

export function goBackIfFlagNotEnabled(flagId: FeatureFlagName): FlagEnabledFn {
  return function redirectIfFlagNotEnabled(nextState) {
    if (!featureFlags.isEnabled(flagId)) {
      const nextURL = nextState.location.pathname;
      diagnostics.breadcrumb(`Not going to ${nextURL} because flag ${flagId} is off.`);
      return hashHistory.goBack();
    }
  };
}

export function goBackIfDeploymentConfigNotEnabled(configIds: string[]): FlagEnabledFn {
  return function redirectIfDeploymentConfigNotEnabled(nextState) {
    for (const configId of configIds) {
      if (!_.get(deploymentConfig.get(), configId)) {
        const nextURL = nextState.location.pathname;
        diagnostics.breadcrumb(`Not going to ${nextURL} because deployment config ${configId} is off.`);
        return hashHistory.goBack();
      }
    }
  };
}

export function goBackIfAllDeploymentConfigsDisabled(configIds: string[]): FlagEnabledFn {
  return function redirectIfAllDeploymentConfigsDisabled(nextState) {
    const configValues = _.map(configIds, configId => _.get(deploymentConfig.get(), configId));
    const hasAllConfigsDisabled = _.every(configValues, configValue => !configValue);
    const nextURL = nextState.location.pathname;

    if (hasAllConfigsDisabled) {
      diagnostics.breadcrumb(`Not going to ${nextURL} because all of the following configs are disabled: ${configIds}`);
      return hashHistory.goBack();
    }
  };
}

export function goBackIfLoanTypeNotEnabled(loanType: DM.LoanType): FlagEnabledFn {
  return function redirectIfLoanTypeNotEnabled(nextState) {
    if (!_.includes(deploymentConfig.get().supportedLoanTypes, loanType)) {
      const nextURL = nextState.location.pathname;
      diagnostics.breadcrumb(`Not going to ${nextURL} because loan type ${loanType} is not enabled.`);
      return hashHistory.goBack();
    }
  };
}

export function redirectIfNoCurrentUser() {
  if (!userUtils.getCurrentUser()) {
    diagnostics.breadcrumb(
      `Redirecting from ${States.LOAN_HYDRATION} to ${States.LOGIN} because there is no current user`,
    );
    return hashHistory.push(States.LOGIN);
  }
}

const goToUrl = path => {
  diagnostics.breadcrumb(`Redirecting to ${path}`);
  return globalWrappers.getWindow().history.replaceState(null, '', `#${path}`);
};

export function interimSSOOnEnter(nextState) {
  if (!deploymentConfig.isNewToBank()) {
    const nextURL = nextState.location.pathname;
    diagnostics.breadcrumb(`Not going to ${nextURL} because New To Bank is disabled.`);
    return hashHistory.goBack();
  }

  if (featureFlags.isEnabled('truncatedEConsentForNTB20190522')) {
    diagnostics.breadcrumb(`Going to Signup flow because New To Bank and truncatedEConsentForNTB20190522 are enabled.`);
    return pushWithContext(States.SIGNUP);
  }
}

const HASH = '#/';
const isBaseURL = (location: Location) => {
  return location.href === `${location.origin}/`;
};

const isValidApplicationURL = (location: Location) => {
  const hasHash = _.includes(location.href, HASH);

  if (!hasHash && location.search !== '') {
    return false;
  }
  if (hasHash && location.pathname === '/') {
    return true;
  }
  if (isBaseURL(location)) {
    return true;
  }
  return false;
};

const isOAuthURL = location => {
  return location.hash === `#/${StatesRich.LENDER_OAUTH.path}`;
};

const isMfaURL = location => {
  return location.hash.match(/^#\/mfa|^#\/setup\/mfa/i);
};

const isValidAuthenticatedLoginRoute = location => {
  const validAuthenticatedLoginRoutes = _.concat(
    globalWrappers.getWindow().SHARED_CONSTANTS.VALID_AUTHENTICATED_LOGIN_ROUTES,
    'logout',
  );
  return _.some(validAuthenticatedLoginRoutes, state => _.includes(location.href, state));
};

export const handleInitialRedirect = () => {
  // Bind because we're going to execute it out of context.
  // We use window.history.replaceState because we want to normalize this *before* we get into any react-router
  // URL handling, and because react-router's hashHistory implementation is actively bad.
  const currentUser = userUtils.getCurrentUser();
  const location = globalWrappers.getLocation();
  // backend will determine what's the proper redirect route in this case
  const reentryPageUrl = '/public/redirect/reentry';

  // for loan officer pages we do not want to check if there is a current user
  if (isLoanOfficerPath(location.pathname)) {
    return goToUrl(`${location.pathname}${location.search}`);
  }

  // If we are currently doing loan hydration or validation, direct users to the corresponding loading page
  if (cookieUtils.get('oneTapValidating')) {
    return goToUrl(`/${States.ONE_TAP_LOADING}`);
  } else if (cookieUtils.get('loanHydrating')) {
    return goToUrl(`/${States.LOAN_HYDRATION}`);
  }

  if (currentUser && !currentUser.acceptedTermsAt) {
    const state = (() => {
      if (
        currentUser.loginMethod === LOGIN_METHODS.BORROWER_SAML &&
        featureFlags.isEnabled('ssoJourneyChooser20190531')
      ) {
        return `${States.JOURNEY_CHOOSER}${location.search}`;
      }

      return States.ACCEPT_TERMS;
    })();
    const path = `/${state}`;
    if (!isAtURL(path)) {
      return goToUrl(path);
    }
  } else if (currentUser && !isOAuthURL(location) && !isMfaURL(location)) {
    // If the role is lender, route them to lender reentry page. (for MFA purpose)
    if (currentUser.appRole === 'lender') {
      return globalWrappers.setHref(reentryPageUrl);
    }
    if (!isValidAuthenticatedLoginRoute(location) && !(window.USER_AUTH_LEVEL === 'restricted')) {
      const chooseApplicationPath = `/${States.CHOOSE_APPLICATION}`;
      return goToUrl(chooseApplicationPath);
    }
    return;
  } else if (!currentUser && (isOAuthURL(location) || isMfaURL(location))) {
    return globalWrappers.setHref(reentryPageUrl);
  }

  // Determine if we're in a valid URL to avoid doing any manipulation or rewriting. If we are, stop running.
  if (isValidApplicationURL(location)) {
    return;
  }

  // Rewrite the URL in situations where we known for sure that the backend is sending us something weird.
  const validURL = getValidStateFromQuery(location.search);
  if (validURL && !isAtURL(validURL)) {
    return goToUrl(validURL);
  }

  // In all other cases, let's just try to remap and then react-router will fix it if needed.
  return goToUrl(`${location.pathname}${location.search}`);
};

export const updateLastAttemptedRoute = (nextState, { dispatch }) => {
  const leafRoute = _.last<RouteState>(nextState.routes);
  const { path, name } = leafRoute!;
  dispatch(updateAttemptedRoute({ path, name }));
};
